import type { Dictionary } from "../i18n";

// Русский интерфейс написан как самостоятельный текст, а не как перевод
// английских фраз слово в слово: калька читается как машинный перевод и мешает
// пользоваться продуктом.
export const ru: Dictionary = {
  // Подписи для скринридера и всплывающие подсказки. Их не видно на макете,
  // поэтому они дольше всех оставались английскими — держим их в словаре вместе
  // с остальным текстом, а не в шаблонных строках внутри JSX.
  a11y: {
    archive: "Архивировать {name}",
    boardOptions: "Настройки доски {name}",
    calculateField: "Пересчитать {field}",
    changeEndDate: "Изменить дату окончания для {name}",
    changeStartDate: "Изменить дату начала для {name}",
    clearFieldFor: "Очистить {field} у карточки {item}",
    createIn: "Создать в {name}",
    drag: "Перетащить {name}",
    editStatusName: "Изменить название статуса {name}",
    expandGroup: "Развернуть {group}",
    fallbackField: "поля",
    fallbackOption: "варианта",
    fieldActions: "Действия для поля {name}",
    fieldFor: "{field} для карточки {item}",
    importColumnTarget: "Куда попадёт колонка {column}",
    makeDefaultStatus: "Сделать {name} статусом, с которого начинаются новые карточки",
    newStatusName: "Название нового статуса в группе «{category}»",
    pickCategory: "Выбрать {name}",
    pickColor: "Выбрать цвет {color}",
    rangeEnd: "{field}: конец",
    rangeStart: "{field}: начало",
    rate: "Оценка {position}",
    remove: "Убрать {name}",
    removeAccess: "Убрать доступ для {name}",
    removeFilter: "Убрать фильтр {field} {value}",
    renameCategory: "Переименовать {name}",
    resizeColumn: "Изменить ширину колонки {column}",
    setFieldValue: "Поставить {field} — {value} из {scale} для карточки {item}",
    sortAscending: "Сортировать по возрастанию",
    sortDescending: "Сортировать по убыванию",
    statusFor: "Статус карточки {item}",
    statusRequirements: "Требования статуса {name}",
    typeActions: "Действия для типа {name}",
    typeFor: "Тип карточки {item}",
  },
  access: {
    inheritedCount: "Унаследованный доступ · {count}",
    add: "Добавить",
    comment: "Комментировать",
    copyPrivateLink: "Скопировать приватную ссылку",
    creatorKeeps: "У создателя, владельцев и администраторов рабочей области полный доступ всегда.",
    details: "Подробности доступа",
    edit: "Полный доступ",
    effective: "Фактический доступ",
    everyone: "Все в рабочей области",
    everyoneHint: "Доступ получают все участники; гостям всё равно нужно поделиться отдельно.",
    general: "Общий доступ",
    inherit: "Как у родителя",
    inheritHint: "Работают правила доступа родителя.",
    invite: "Пригласить людей или команды",
    invitePlaceholder: "Пригласить людей или команды…",
    level: "Что можно делать",
    memberToInspect: "Кого проверяем",
    noResults: "Никого не нашлось.",
    path: "Откуда доступ",
    pickMember: "Выберите человека…",
    private: "Только по приглашению",
    privateHint: "Только те люди и команды, что добавлены ниже.",
    readOnlyHere: "Здесь только просмотр — меняется там, откуда доступ приходит.",
    recentChanges: "Последние изменения",
    view: "Смотреть",
    whyHasAccess: "Посмотреть, откуда именно у человека доступ.",
  },
  board: {
    accessShare: "Доступ и ссылки",
    addContext: "Добавить описание…",
    addFilter: "Добавить условие",
    addSort: "Добавить сортировку",
    addToSprint: "Добавить в спринт",
    addWorkItem: "Добавить задачу",
    allFieldsEmpty: "Все поля пустые.",
    allTypes: "Все типы",
    allWork: "Вся работа",
    andOperator: "И",
    applyFilter: "Применить фильтр",
    applyTemplate: "Применить шаблон",
    applyTemplateHint: "Добавит недостающие доске поля, статусы и виды и задаст, откуда начинается работа. Ничего существующего не удаляется, задачи не трогаются.",
    applyTemplateTitle: "Применить шаблон",
    archive: "Архивировать",
    archiveBoard: "Убрать доску",
    archiveBoardHint: "Доска и всё на ней уедет в корзину, а через 30 дней удалится насовсем.",
    archiveView: "Убрать вид",
    archiving: "Архивируем…",
    back: "Назад",
    boardName: "Название доски",
    boards: "Доски",
    calculate: "Посчитать",
    cardFieldEmpty: "{field}: пусто",
    cardSize: "Размер карточек",
    changeViewType: "Сменить тип вида",
    changesApply: "Изменения применяются к этому виду сразу.",
    chooseDateField: "Выберите поле с датой",
    chooseSpace: "Выберите, где будет жить доска. Её можно перенести позже, не потеряв задачи.",
    clear: "Очистить",
    clearFilter: "Сбросить фильтр",
    clearSelection: "Снять выделение",
    closeViewSettings: "Закрыть настройку вида",
    collapseEmpty: "Сворачивать пустые колонки",
    collapseEmptyHint: "Колонки без задач сжимаются в полоску",
    copied: "Скопировано",
    copyLink: "Скопировать ссылку на вид",
    createBoard: "Создать доску",
    createItem: "Создать задачу",
    createTypeFirst: "Сначала создайте тип задачи",
    customField: "Своё поле",
    customizeView: "Настроить вид",
    dashboard: "Дашборд",
    decreaseSort: "Ниже в порядке сортировки",
    defaultBadge: "по умолчанию",
    defaultView: "Вид по умолчанию",
    description: "Описание",
    document: "Документ",
    dropHere: "Перетащите задачу сюда",
    duplicateView: "Сделать копию вида",
    durationExample: "например: 2ч 30м",
    emptyBoard: "Пустая доска",
    emptyFirst: "Пустые в начале",
    emptyFolder: "Папка пуста.",
    emptyLast: "Пустые в конце",
    exactValue: "Точное значение",
    exportCsv: "Скачать CSV",
    exportExcel: "Скачать Excel",
    exportJson: "Скачать JSON",
    fields: "Поля",
    fieldsShown: "показано {count}",
    filter: "Фильтр",
    filtered: "С фильтром",
    folderName: "Название папки",
    form: "Форма",
    formulaGlobal: "Формула меняется сразу везде, где используется это поле.",
    group: "Группировка",
    groupBucket: "Ширина корзины",
    groupBucketExact: "Точное значение",
    groupBucketQuarter: "{quarter} кв. {year}",
    groupBucketRange: "{start} – {end}",
    groupBucketStep: "По {size}",
    groupBucketUnit: {
      day: "По дням",
      month: "По месяцам",
      quarter: "По кварталам",
      week: "По неделям",
      year: "По годам",
    },
    groupBy: "Группировать по",
    groupFieldFallback: "Поле",
    groupNoValue: "{field}: без значения",
    groupWith: "Группировка: {field}",
    hide: "Скрыть",
    increaseSort: "Выше в порядке сортировки",
    itemName: "Название задачи",
    itemNamePlaceholder: "Название задачи…",
    kanban: "Канбан",
    lastView: "Последний вид",
    layout: "Вид карточек",
    list: "Список",
    loading: "Загрузка…",
    manual: "Вручную",
    manualOrder: "Порядок вручную",
    name: "Название",
    newBoardHint: "Создайте доску внутри пространства — на ней появятся виды и задачи.",
    newItem: "Новая задача",
    noActiveViews: "Видов пока нет",
    noBoards: "Досок пока нет",
    noCalculation: "Без подсчёта",
    noCustomFields: "Своих полей нет",
    noFields: "Полей нет.",
    noGrouping: "Без группировки",
    noItems: "Задач пока нет",
    noMatchingIcons: "Подходящих иконок нет",
    noOptions: "Вариантов нет.",
    noType: "Без типа",
    noValue: "Без значения",
    none: "Нет",
    openAndFix: "Открыть задачу и поправить",
    optionalContext: "Описание доски (по желанию)",
    parent: "Родитель",
    personalView: "Только для меня",
    personalViewBadge: "Этот вид видите только вы",
    personalViewCannotBeDefault: "Вид, который видите только вы, не может быть видом по умолчанию",
    personalViewHint: "Никто больше его не увидит. Удобно попробовать фильтр, не меняя доску для всех.",
    pinFirstColumn: "Закрепить первую колонку",
    pinFirstColumnHint: "Колонка с названием остаётся на месте при прокрутке",
    preparing: "Готовим…",
    remove: "Убрать",
    removeFromBoard: "Убрать с доски",
    removeFromBoardArchives: "Хотя бы одна выбранная карточка есть только на этой доске. Если убрать её отсюда, карточка уедет в корзину.",
    removeFromBoardKeeps: "Выбранные карточки уйдут с этой доски и останутся на остальных досках, где они есть.",
    removeFromBoardTitle: { few: "Убрать {count} карточки с доски?", many: "Убрать {count} карточек с доски?", one: "Убрать карточку с доски?", other: "Убрать {count} карточки с доски?" },
    removeSort: "Убрать сортировку",
    repairView: "Восстановить вид по умолчанию",
    repairViewHint: "Доска есть, но её вид потерялся.",
    replay: "Перемотка",
    report: "Отчёт",
    roadmap: "Дорожная карта",
    rowSize: "Высота строк",
    saveFormula: "Сохранить формулу",
    scheduledItems: "Задачи с датами",
    selectSpace: "Выберите пространство",
    setDefaultView: "Сделать видом по умолчанию",
    settings: "Настройки доски",
    show: "Показать",
    showClosed: "Показывать закрытые задачи",
    showClosedHint: "Вместе с теми, что в статусе «Закрыто»",
    showColumnColors: "Красить заголовки колонок",
    showColumnColorsHint: "Заголовки берут цвет своей группы",
    showEmptyFields: "Показывать пустые поля",
    showEmptyFieldsHint: "Поля без значения тоже видны на карточке",
    similarByTitle: "похоже",
    sizeCompact: "Компактно",
    sizeDefault: "Обычно",
    sizeLarge: "Крупно",
    sort: "Сортировка",
    sortFields: { few: "{count} поля", many: "{count} полей", one: "{count} поле", other: "{count} поля" },
    sortWithCount: "Сортировка · {count}",
    space: "Пространство",
    spaceRoot: "Корень пространства",
    spacesAndBoards: "{spaces} · {boards}",
    sprintBoardName: "Название спринт-доски",
    startFirstItem: "Создайте первую задачу.",
    startFrom: "Взять за основу",
    timeInStatusAt: "Сколько задача была в этом статусе на момент перемотки",
    timeline: "Таймлайн",
    timelineDateField: "Поле с датой для таймлайна",
    timelineDateFieldHint: "По этому полю задачи показываются на таймлайне, двигаются и меняют длину.",
    timelineScale: "Масштаб таймлайна",
    title: "Название",
    today: "Сегодня",
    total: "Итого",
    transitionBlocked: "Выполните требования ниже и попробуйте перевести ещё раз.",
    typeHasNoFields: "У этого типа задач нет полей.",
    untitledItem: "Задача без названия",
    view: "Вид",
    viewCopyName: "{name} — копия",
    viewName: "Название вида",
    viewSettings: "Настройка вида",
    workItem: "Задача",
    workItemTitle: "Название задачи",
    wrapText: "Переносить текст",
    wrapTextHint: "Длинные значения показываются в несколько строк",
    writePage: "Напишите текст…",
  },
  boardActions: {
    accessShare: "Доступ и ссылки",
    applyTemplate: "Применить шаблон",
    archiveBoard: "Архивировать доску",
    importItems: "Импорт задач",
    settings: "Настройки",
    statuses: "Статусы",
  },
  colors: {
    red: "Красный",
    blue: "Синий",
    brown: "Коричневый",
    color: "Цвет",
    default: "По умолчанию",
    gray: "Серый",
    green: "Зелёный",
    highlight: "Заливка",
    none: "Без цвета",
    orange: "Оранжевый",
    purple: "Фиолетовый",
    text: "Текст",
    textColor: "Цвет текста",
    yellow: "Жёлтый",
  },
  connect: {
    clientIdLabel: "клиент {id}",
    connectedSince: "подключено {when}",
    allow: "Подключить",
    asYou: "Приложение будет действовать от вашего имени, {email}.",
    asYouUnknown: "Приложение будет действовать от вашего имени.",
    boundary: "Оно никогда не увидит больше, чем видите вы, и вы можете отключить его в разделе «Интеграции».",
    connectedApps: "Подключённые приложения",
    connectedAppsEmpty: "Пока ничего не подключено. Одобренные приложения появятся здесь.",
    connectedAppsHint: "Ассистенты и приложения, которым вы разрешили вход в Impacto.",
    decline: "Не сейчас",
    disconnect: "Отключить",
    expired: "Этот запрос больше не действует",
    expiredHint: "Начните подключение заново в приложении, которое его запросило.",
    failed: "Не удалось выполнить запрос.",
    lastUsed: "Последний раз: {when}",
    neverUsed: "Ещё не использовалось",
    scopeRead: "Видеть всё, что видите вы: доски, задачи, комментарии и спринты.",
    scopeSprints: "Планировать спринты: добавлять и убирать работу, запускать и закрывать.",
    scopeWorkspaceAdmin: "Менять саму рабочую область: доски, статусы, виды, поля, типы задач и состав команды.",
    scopeUnknown: "Доступ, который приложение не объяснило.",
    scopeWorkItems: "Создавать и менять задачи — ровно те, которые можете менять вы.",
    signInFirst: "Войдите, чтобы продолжить подключение.",
    title: "{app} хочет подключиться к Impacto",
  },
  common: {
    accessShareFor: "Доступ и доступность: {name}",
    all: "Все",
    cancel: "Отмена",
    checked: "Отмечено",
    clear: "Очистить",
    close: "Закрыть",
    copied: "Скопировано",
    copy: "Копировать",
    create: "Создать",
    creating: "Создаём…",
    delete: "Удалить",
    done: "Готово",
    edit: "Изменить",
    edit2: "Изменить",
    loading: "Загрузка…",
    mobileSidebar: "Показывает боковую панель на мобильном.",
    more: "Ещё",
    noMatches: "Ничего не нашлось",
    notSelected: "Не выбрано",
    optionsFor: "Действия: {name}",
    save: "Сохранить",
    search: "Поиск…",
    send: "Отправить",
    shiftEnter: "Shift+Enter — новая строка",
    sidebar: "Боковая панель",
    someone: "Кто-то",
    toggleSidebar: "Свернуть или развернуть панель",
    toggleSidebar2: "Свернуть или развернуть панель",
    unchecked: "Не отмечено",
    view: "Смотреть",
  },
  editor: {
    editLink: "Изменить ссылку",
    openLink: "Открыть ссылку",
    removeLink: "Убрать ссылку",
    alignImageCenter: "По центру",
    codeLanguage: "Язык блока кода",
    deleteImage: "Удалить картинку",
    deleteTable: "Удалить таблицу",
    downloadImage: "Скачать картинку",
    imageOptions: "Что сделать с картинкой",
    merge: "Объединить ячейки",
    sortAscending: "Сортировать по возрастанию",
    sortDescending: "Сортировать по убыванию",
    split: "Разделить ячейки",
    subCallout: "Цитата с выделением",
    subChecklist: "Пункты с галочками",
    subCode: "Код с подсветкой",
    subFile: "Загрузить файл",
    subHorizontal: "Горизонтальная линия",
    subImage: "Загрузить картинку",
    subKeySection: "Важный раздел",
    subOrdered: "Пункты по порядку",
    subParagraph: "Обычный абзац",
    subSubsection: "Подраздел",
    subTable: "3×3 со строкой заголовков",
    subTopSection: "Раздел верхнего уровня",
    subUnordered: "Пункты списком",
    alignCenter: "По центру",
    alignLeft: "По левому краю",
    alignRight: "По правому краю",
    blockOptions: "Что сделать с блоком",
    bold: "Полужирный",
    bulletList: "Список",
    code: "Код в строке",
    codeBlock: "Блок кода",
    delete: "Удалить",
    divider: "Разделитель",
    duplicate: "Дублировать",
    file: "Файл",
    heading1: "Заголовок 1",
    heading2: "Заголовок 2",
    heading3: "Заголовок 3",
    highlight: "Выделение цветом",
    image: "Картинка",
    insertBelow: "Вставить блок ниже",
    insertBlock: "Вставить блок",
    italic: "Курсив",
    link: "Ссылка",
    linkHint: "Вставьте ссылку — пустое поле её убирает",
    moveDown: "Вниз",
    moveUp: "Вверх",
    numberedList: "Нумерованный список",
    quote: "Цитата",
    reload: "Перезагрузить",
    set: "Применить",
    strikethrough: "Зачёркнутый",
    table: "Таблица",
    text: "Текст",
    todoList: "Список задач",
    turnInto: "Превратить в",
    underline: "Подчёркнутый",
  },
  emoji: {
    activities: "Занятия",
    animals: "Животные и природа",
    flags: "Флаги",
    food: "Еда и напитки",
    none: "Ничего не нашлось.",
    objects: "Предметы",
    people: "Люди",
    recent: "Недавние",
    search: "Найти эмодзи…",
    smileys: "Смайлы и эмоции",
    symbols: "Символы",
    travel: "Путешествия и места",
  },
  crash: {
    description: "Что-то в интерфейсе перестало работать. Сбой уже отправлен. Обычно достаточно попробовать ещё раз — перезагрузка потеряет то, что не успело сохраниться.",
    reload: "Перезагрузить страницу",
    retry: "Попробовать ещё раз",
    title: "Не удалось отрисовать этот экран",
  },
  fieldDialog: {
    archiveHint: "Поле пропадёт из активной схемы. Значения на задачах останутся сохранёнными — для истории и на случай восстановления.",
    numberMax: "не больше",
    numberMin: "не меньше",
    numberRange: "Допустимые значения",
    numberRangeHint: "Пустое поле — правила нет. Значение за границами отвергается везде, где его вводят: в форме, в таблице, при импорте и у агента.",
    numberStep: "шаг",
    personRole: "Чья это роль",
    personRoleAnyone: "Любой участник",
    personRoleHint: "Люди из этой команды предлагаются первыми. Остальных по-прежнему можно выбрать — подменить коллегу это обычная работа.",
    scopeBoard: "Доска",
    scopeFolder: "Папка",
    scopeSpace: "Пространство",
    scopeWorkspace: "Рабочая область",
    allItemTypes: "Все типы задач",
    archiveField: "Архивировать поле",
    archiveOptionConfirm: "Архивировать «{option}»?",
    archiving: "Архивируем…",
    chooseField: "Выберите поле",
    chooseRelation: "Выберите связь",
    couldNotArchive: "Не удалось архивировать поле.",
    couldNotArchiveOption: "Не удалось архивировать вариант.",
    couldNotSave: "Не удалось сохранить поле.",
    fallbackFieldName: "Поле",
    fieldKey: "Ключ поля",
    fieldName: "Название поля",
    inherited: "Наследуется",
    inverseFallback: "Как связь читается на задаче, на которую она указывает. Если оставить пустым — «{name} для».",
    keyDuplicate: "Такой ключ уже занят.",
    keyTooLong: "Не длиннее 48 символов.",
    keyTooShort: "Минимум 2 символа.",
    nameTooLong: "Не длиннее 80 символов.",
    nameTooShort: "Минимум 2 символа.",
    newField: "Новое поле",
    rollupAnyStatus: "Любой статус — считаются все связанные задачи.",
    rollupOnlyStatuses: "Считаются только связанные задачи в этих категориях статусов.",
    saveChanges: "Сохранить изменения",
    saving: "Сохраняем…",
    settings: "Настройки поля",
    systemCannotArchive: "Системное поле нельзя архивировать",
    systemReadOnly: "Системное поле — только чтение",
    typesSelected: {
      one: "Выбран {count} тип",
      few: "Выбрано {count} типа",
      many: "Выбрано {count} типов",
      other: "Выбрано {count} типа",
    },
    unnamedOption: "Вариант",
    inversePlaceholder: "Дочерние",
    acyclic: "Тогда задача никогда не окажется внутри самой себя, какой бы длинной ни была цепочка.",
    appliedTo: "Применяется к",
    archive: "Убрать",
    archiveConfirm: "Убрать это поле?",
    archiveOption: "Убрать вариант",
    calculate: "Считать",
    canPointAt: "Может указывать на",
    chooseOptionColor: "Выбрать цвет варианта",
    chooseTypes: "Выберите типы",
    clearValues: "Очистить значения",
    close: "Закрыть",
    closeHistory: "Закрыть историю поля",
    closeSettings: "Закрыть настройки поля",
    countStatuses: "Считать статусы",
    describe: "Задайте название поля, где оно показывается и как себя ведёт.",
    history: "История поля",
    historyShort: "История",
    leaveEmptyAnyType: "Оставьте пустым — подойдёт любой тип задачи.",
    moveValuesTo: "Перенести значения в",
    movedBeforeArchive: "Уже проставленные значения можно перенести в другой вариант, прежде чем убирать этот.",
    nameFromOtherSide: "Как называется с другой стороны",
    newOption: "Новый вариант",
    noLocations: "Мест нет.",
    noTypes: "Типов нет.",
    onlyOnce: "На задачу можно указать только один раз",
    option: "Вариант",
    optionName: "Название варианта",
    options: "Варианты",
    ratingScale: "Шкала оценки",
    recentChanges: "Последние изменения",
    relation: "Связь",
    removeOption: "Убрать вариант",
    replacementOption: "Вариант на замену",
    selectVisibility: "Выберите, где это поле показывается.",
    showAsProgress: "Показывать полосой прогресса",
    sourceField: "Поле-источник",
    thisIsHierarchy: "Эта связь образует иерархию",
    twoCannotLink: "Две задачи не могут указывать на одну и ту же через это поле.",
    types: "Типы",
    untitled: "Поле без названия",
    visibleIn: "Видно в",
    workspace: "Рабочая область",
  },
  rollupOperator: {
    avg: "Среднее",
    count: "Количество",
    empty: "Пустых",
    filled: "Заполненных",
    max: "Максимум",
    min: "Минимум",
    sum: "Сумма",
  },
  fieldType: {
    checkbox: "Галочка",
    checkboxHint: "Для простых «да/нет» и отметок «готово».",
    currency: "Деньги",
    currencyHint: "Для бюджетов, затрат, выручки и других сумм.",
    date: "Дата",
    dateHint: "Для дедлайна, даты запуска или важной вехи.",
    dateRange: "Период",
    dateRangeHint: "Для расписаний, плановых окон и интервалов дат.",
    duration: "Длительность",
    durationHint: "Для трудозатрат, затраченного времени, сроков по SLA.",
    email: "Почта",
    emailHint: "Для контактных адресов и переписки.",
    formula: "Формула",
    formulaHint: "Для значений, которые считаются из других полей той же задачи.",
    multiSelect: "Несколько из списка",
    multiSelectHint: "Когда у задачи может быть сразу несколько меток или категорий.",
    number: "Число",
    numberHint: "Для количества, баллов, оценок и всего, что потом считают.",
    percent: "Проценты",
    percentHint: "Для конверсии, уверенности, готовности и других долей.",
    phone: "Телефон",
    phoneHint: "Для контактных телефонов.",
    rating: "Оценка звёздами",
    ratingHint: "Для быстрой качественной оценки звёздами или короткой шкалой.",
    relatedItem: "Связанная задача",
    relatedItemHint: "Чтобы привязать одну задачу: родителя, источник или зависимость.",
    relatedItems: "Связанные задачи",
    relatedItemsHint: "Чтобы привязать несколько задач: зависимости, ссылки, суммирование.",
    rollup: "Сводка по связям",
    rollupHint: "Чтобы сложить значения задач, привязанных через поле связи.",
    select: "Один из списка",
    selectHint: "Для одного значения из заданного списка — приоритет, стадия.",
    text: "Текст",
    textHint: "Для короткого текста в одну строку.",
    textarea: "Длинный текст",
    textareaHint: "Для заметок и текста в несколько строк.",
    type: "Тип задачи",
    typeHint: "Чтобы показать или использовать тип задачи в видах и формах.",
    url: "Ссылка",
    urlHint: "Для ссылок на документы, тикеты и внешние ресурсы.",
    user: "Человек",
    userHint: "Для одного ответственного, владельца или ревьюера.",
    users: "Люди",
    usersHint: "Когда людей может быть несколько: исполнители, наблюдатели, ответственные.",
  },
  fields: {
    locations: "Где показывается",
    allDescription: "Все поля, которые можно добавить задаче.",
    allTitle: "Все поля",
    created: "Создано",
    name: "Название",
    origin: "Откуда взялось",
    originCustom: "Завели здесь",
    originDefault: "Идёт с Impacto",
    originSystem: "Встроенное",
    originTemplate: "Из шаблона",
    typeDescription: "Поля, которые есть только у некоторых типов задач.",
    workspaceDescription: "Поля, доступные на всех досках этой рабочей области.",
    history: "История поля",
    noChanges: "Пока ничего не менялось.",
    recentChangesHint: "Последние изменения этого поля.",
    allFields: "Все поля",
    allTypes: "Все типы полей",
    fieldType: "Тип поля",
    newField: "Новое поле",
    noMatches: "Ничего не нашлось",
    search: "Найти поле",
    searchPlaceholder: "Найти поле…",
    sections: "Разделы полей",
    title: "Поля",
    tryAnother: "Попробуйте другой запрос или другой тип поля.",
    typeFields: "Поля типа",
    whatItIs: "Поля, которые можно добавить любой задаче — одно и то же поле работает на всех досках.",
    workspace: "Рабочая область",
    workspaceFields: "Поля рабочей области",
  },
  filterBuilder: {
    addCondition: "Добавить условие",
    advancedSyntaxOnly: "В этой формуле сложный синтаксис, править её можно только здесь.",
    builder: "Конструктор",
    checked: "Отмечено",
    condition: "Условие",
    conditionActions: "Действия с условием",
    conditionField: "Поле условия",
    conditionOperator: "Оператор условия",
    conditionValue: "Значение условия",
    couldNotParse: "Эту формулу не удалось разобрать.",
    couldNotRead: "Это выражение не удалось прочитать.",
    editInFormula: "Открыть как формулу",
    emptyHintBoardWidget: "Без фильтра — виджет охватывает все элементы доски.",
    emptyHintSprintWidget: "Без фильтра — виджет охватывает все элементы спринта.",
    emptyHintView: "Фильтров пока нет — показаны все элементы. Добавьте условие ниже.",
    enterValue: "Введите значение",
    field: "Поле",
    formula: "Формула",
    joinAll: "все",
    joinAny: "любое",
    match: "Совпадает",
    matchMode: "Как совпадает",
    nestedGroups: "В этом выражении условия вложены группами. В режиме формулы оно редактируется целиком.",
    notShown: "Эту формулу не показать в конструкторе",
    ofConditionsBelow: "из условий ниже.",
    opAtLeast: "не меньше",
    opAtMost: "не больше",
    opEmpty: "пусто",
    opEquals: "равно",
    opFilled: "заполнено",
    opGreater: "больше",
    opLess: "меньше",
    opNotEquals: "не равно",
    orOperator: "ИЛИ",
    removeCondition: "Убрать условие",
    resetBuilder: "Собрать условие заново",
    selectValue: "Выберите значение",
    unchecked: "Не отмечено",
    unsupportedSyntax: "В ней синтаксис, который конструктор не покажет, не изменив смысла.",
    useFixedValue: "Взять фиксированное значение",
    useFormula: "Взять формулу",
    value: "Значение",
  },
  formula: {
    calculating: "Считаем",
    errEmptyReference: "Ссылка на поле пустая.",
    errSelfReference: "Формула не может ссылаться сама на себя.",
    errSelfThroughRelation: "Формула не может дотянуться до себя через связь.",
    errUnfinished: "Ссылка на поле не дописана.",
    example: "Пример",
    exampleWithAlias: "Пример ({alias})",
    fields: "Поля",
    insert: "Вставить",
    leafDirect: "Значение поля «{field}» у текущей карточки.{scope}",
    leafReverseRelation: "Карточки, которые ссылаются на эту через «{relation}», и их поле «{field}». Оберните в sum(), avg() или count(), чтобы свести список в одно число.{scope}",
    leafViaRelation: "Поле «{field}» у карточек, связанных через «{relation}». Оберните в sum(), avg() или count(), чтобы свести список в одно число.{scope}",
    noMatches: "Ничего не нашлось.",
    opAdd: "Складывает числа, а если хоть одна сторона — текст, склеивает в текст.",
    opAnd: "Истина, только когда обе стороны истинны.",
    opDivide: "Делит левое число на правое. Деление на ноль даёт пустое значение.",
    opEqual: "Истина, когда обе стороны равны.",
    opGreater: "Истина, когда слева больше.",
    opGreaterEqual: "Истина, когда слева больше или столько же.",
    opLess: "Истина, когда слева меньше.",
    opLessEqual: "Истина, когда слева меньше или столько же.",
    opMultiply: "Умножает два числа.",
    opNot: "Переворачивает «да» в «нет» и наоборот.",
    opNotEqual: "Истина, когда стороны различаются.",
    opOr: "Истина, когда истинна хотя бы одна сторона.",
    opSubtract: "Вычитает правое число из левого.",
    operators: "Операторы",
    reads: "Читается как",
    referencedVia: "Ссылаются через «{relation}»",
    sampleInputs: "Пример значений",
    scopeNote: " Есть только у некоторых типов карточек — у остальных пусто.",
    search: "Поиск",
    selectToSeeDetails: "Выберите поле или функцию — покажем, что она делает.",
    syntax: "Синтаксис",
    title: "Формула",
    typeReferencedBy: "{type} · ссылаются через «{relation}»",
    typeSomeTypes: "{type} · не у всех типов",
    typeViaRelation: "{type} · через «{relation}»",
  },
  iconPicker: {
    choose: "Выбрать значок",
    clearSearch: "Очистить поиск",
    library: "Библиотека значков",
    search: "Поиск: задача, баг, релиз…",
    suggested: "Подходящие",
  },
  importItems: {
    phraseCreated: {
      one: "создана {count} задача",
      few: "создано {count} задачи",
      many: "создано {count} задач",
      other: "создано {count} задачи",
    },
    phraseHistoryItems: {
      one: "{count} задача",
      few: "{count} задачи",
      many: "{count} задач",
      other: "{count} задачи",
    },
    phraseHistoryRestored: {
      one: "{count} возвращена",
      few: "{count} возвращены",
      many: "{count} возвращено",
      other: "{count} возвращено",
    },
    phraseHistoryUpdated: {
      one: "{count} обновлена",
      few: "{count} обновлены",
      many: "{count} обновлено",
      other: "{count} обновлено",
    },
    phraseNew: {
      one: "{count} новая задача",
      few: "{count} новые задачи",
      many: "{count} новых задач",
      other: "{count} новой задачи",
    },
    phraseRestore: {
      one: "{count} задача вернётся из корзины",
      few: "{count} задачи вернутся из корзины",
      many: "{count} задач вернутся из корзины",
      other: "{count} задачи вернутся из корзины",
    },
    phraseRestored: {
      one: "{count} задача возвращена из корзины",
      few: "{count} задачи возвращены из корзины",
      many: "{count} задач возвращено из корзины",
      other: "{count} задачи возвращено из корзины",
    },
    phraseUpdate: {
      one: "{count} уже существующая задача будет обновлена",
      few: "{count} уже существующие задачи будут обновлены",
      many: "{count} уже существующих задач будут обновлены",
      other: "{count} уже существующие задачи будут обновлены",
    },
    phraseUpdated: {
      one: "обновлена {count} задача",
      few: "обновлено {count} задачи",
      many: "обновлено {count} задач",
      other: "обновлено {count} задачи",
    },
    skippedRows: {
      one: "{count} строку импортировать не удалось, она пропущена.",
      few: "{count} строки импортировать не удалось, они пропущены.",
      many: "{count} строк импортировать не удалось, они пропущены.",
      other: "{count} строки импортировать не удалось, они пропущены.",
    },
    confirmUndo: "Отменить этот импорт?",
    couldNotApplyMapping: "Не удалось применить это сопоставление.",
    couldNotImport: "Не удалось импортировать этот файл.",
    couldNotRead: "Не удалось прочитать этот файл.",
    couldNotUndo: "Не удалось отменить этот импорт.",
    finished: "Импорт завершён",
    importedFile: "Импортированный файл",
    importN: {
      one: "Импортировать {count} задачу",
      few: "Импортировать {count} задачи",
      many: "Импортировать {count} задач",
      other: "Импортировать {count} задачи",
    },
    nothingToImport: "Импортировать нечего",
    onlyRefreshed: "Обновились уже существующие задачи — нового ничего не добавилось.",
    restoreHint: "Задачи возвращаются ровно туда, где были, со своим ключом и историей — обновляются только сопоставленные значения.",
    reviewTitle: "Проверьте, что произойдёт",
    reviewHint: "Пока ничего не изменилось. Поправьте, если что-то не так, и импортируйте.",
    startHint: "Загрузите CSV-выгрузку из другого трекера. То, что он добавит, можно будет отменить.",
    title: "Импорт задач",
    undoAll: "Всё, что добавил этот импорт, можно вернуть назад.",
    undoPartial: "То, что импорт добавил, можно вернуть. То, что он поменял в уже существовавших задачах, останется.",
    updateHint: "Уже существующие задачи сохраняют статус и место на доске — обновляются только сопоставленные значения.",
    willArchive: {
      one: "{count} задача уйдёт в корзину, откуда её ещё можно достать.",
      few: "{count} задачи уйдут в корзину, откуда их ещё можно достать.",
      many: "{count} задач уйдут в корзину, откуда их ещё можно достать.",
      other: "{count} задачи уйдут в корзину, откуда их ещё можно достать.",
    },
    willKeep: {
      one: "{count} уже существовавшая задача останется с теми значениями, что записал импорт. Прежние значения нигде не сохранялись, откат их не вернёт.",
      few: "{count} уже существовавшие задачи останутся с теми значениями, что записал импорт. Прежние значения нигде не сохранялись, откат их не вернёт.",
      many: "{count} уже существовавших задач останутся с теми значениями, что записал импорт. Прежние значения нигде не сохранялись, откат их не вернёт.",
      other: "{count} уже существовавшие задачи останутся с теми значениями, что записал импорт. Прежние значения нигде не сохранялись, откат их не вернёт.",
    },
    chooseAnother: "Выбрать другой файл",
    columns: "Колонки",
    drop: "Перетащите CSV сюда или выберите файл",
    earlier: "Прошлые импорты",
    exportHint: "Выгрузка из ClickUp, Jira, Linear, Asana или таблицы — колонки сопоставятся сами.",
    fieldsOnBoard: "Поля этой доски",
    linkToOriginal: "Ссылка на оригинал",
    originalId: "Исходный ID",
    pickTitle: "Укажите, в какой колонке название, — без него импортировать нечего.",
    skipColumn: "Пропустить колонку",
    undo: "Отменить",
    undoImport: "Отменить импорт",
    undone: "Отменён",
    undoneMessage: "Импорт отменён. То, что он добавил, лежит в корзине — оттуда это можно вернуть.",
  },
  inbox: {
    allCaughtUp: "Всё прочитано.",
    markAllRead: "Отметить всё прочитанным",
    title: "Входящие",
    whatItIs: "Сюда приходят упоминания, назначения и смены статусов ваших задач.",
  },
  integrations: {
    connect: {
      description:
        "Ассистент попросит вас войти и увидит ровно то же, что видите вы: доски, связи, что от чего зависит — и, если разрешите, сможет создавать и двигать задачи.",
      ready:
        "«{name}» готов и уже подставлен в настройку ниже. Секрет показывается один раз: если уйти со страницы, останется только создать новый токен.",
      testConnection: "Проверить подключение",
      testConnected: "Подключено — доступно инструментов: {count}.",
      testEmpty: "Сервер ответил, но не дал ни одного инструмента.",
      testFailed: "Не удалось достучаться до сервера.",
      title: "Подключить ассистента",
      tokenLabel: "Токен",
      tryWith: "Попробуйте так",
    },
    plugin: {
      "claude-codeHint": "Клик открывает Claude Code с уже набранной установкой — прочитайте и нажмите Enter. Она распакует плагин в вашу папку навыков, которую читает любая версия Claude Code при старте.",
      claudeMarketplace: "Или через маркетплейс — тогда плагин будет обновляться сам. Нужен Claude Code 2.1.224 или новее: старые отказываются ставить архив и говорят, что тип источника не поддерживается.",
      codexHint: "У Codex нажимать нечего, поэтому здесь одна команда: она подключает эту рабочую область — вход попросит подтвердить — и кладёт навыки в вашу папку навыков.",
      copied: "Скопировано",
      copyCommand: "Скопировать команду",
      cursorHint: "Откроет Cursor и добавит подключение. Навыки — история Claude Code и Codex; Cursor получает сам трекер.",
      description: "Ассистент узнаёт саму рабочую область, а не только её адрес: как называются доски, что здесь значит статус, как завести задачу, чтобы её не пришлось разбирать заново.",
      install: "Установить в {app}",
      showSteps: "Показать, что выполнится",
      title: "Поставить плагин",
      vscodeHint: "Откроет VS Code и добавит подключение. Навыки — история Claude Code и Codex; VS Code получает сам трекер.",
    },
    tokens: {
      noExpiry: "без срока — стоит ротировать",
      actingAs: "от имени {name}",
      trail: "Что делал",
      trailEmpty: "Этот токен пока ничего не делал.",
      create: {
        expiry: "Срок действия",
        expiryDays: {
          one: "Через {count} день",
          few: "Через {count} дня",
          many: "Через {count} дней",
          other: "Через {count} дня",
        },
        expiryHint: "У служебного токена всегда есть срок: секрет, который никто никогда не меняет, переживает и причину, по которой его завели. Ротация до этой даты: создать новый, подставить его, потом отозвать этот.",
        expiryYear: "Через год",
        actsAs: "От чьего имени",
        actsAsServiceHint: "Свой доступ, который выдаётся на экране «Команда». Продолжит работать, когда люди приходят и уходят.",
        actsAsYou: "От вашего",
        actsAsYouHint: "Токен видит ровно то же, что видите вы, и не больше.",
        canDo: "Что ему можно",
        canDoHint: "Чтение разрешено всегда. Остальное — только если отметить.",
        description: "Назовите по тому, что будет им пользоваться, — тогда отозвать будет просто.",
        failed: "Не удалось создать токен.",
        name: "Название",
        sprintsDescription: "Запускать и закрывать спринты, менять их состав.",
        sprintsLabel: "Менять спринты",
        submit: "Создать токен",
        title: "Новый служебный токен",
        workItemsDescription: "Создавать задачи, двигать их по статусам, комментировать.",
        workItemsLabel: "Менять задачи",
        workspaceDescription: "Менять доски, статусы, виды, поля, типы задач и команду.",
        workspaceLabel: "Менять рабочую область",
      },
      description:
        "Токен действует от вашего имени или от имени сервисного аккаунта с экрана «Команда» — и не дотянется дальше. Заведите каждой интеграции свой: тогда одну можно отключить, не трогая остальные.",
      empty: "Токенов пока нет.",
      expires: "истекает {when}",
      lastUsed: "последний раз {when}",
      never: "ни разу не использован",
      new: "Новый токен",
      revoke: "Отозвать",
      title: "Служебные токены",
    },
  },
  item: {
    activityEmpty: "пусто",
    activityArchived: "убрал(а) задачу в корзину",
    activityChanged: "изменил(а) поле",
    activityCreated: "создал(а) задачу",
    activityField: "поле",
    activityFrom: "из",
    activityMovedTo: "перевёл(а) в",
    activityRestored: "вернул(а) задачу из корзины",
    activitySprintAdded: "добавил(а) задачу в спринт",
    activitySprintRemoved: "убрал(а) задачу из спринта",
    relationProgress: "готово {done} из {total}",
    writeComment: "Напишите комментарий…",
    activity: "История",
    addDescription: "Добавьте описание…",
    archive: "Убрать в корзину",
    archiveConfirm: "Убрать задачу в корзину?",
    comments: {
      addReaction: "Поставить реакцию",
      attach: "Прикрепить файлы",
      bold: "Полужирный",
      bulletList: "Список",
      cancelEdit: "Отменить (Esc)",
      cancelReply: "Не отвечать",
      code: "Код",
      copyText: "Скопировать текст",
      delete: "Удалить комментарий",
      deleteConfirm: "Удалить комментарий?",
      deleteDescription: "Комментарий и вложения к нему пропадут навсегда.",
      edit: "Изменить комментарий",
      editPlaceholder: "Изменить комментарий…",
      emoji: "Вставить эмодзи",
      empty: "Здесь пока пусто. Напишите первый комментарий.",
      italic: "Курсив",
      moreReactions: "Ещё реакции",
      numberedList: "Нумерованный список",
      reply: "Ответить",
      strikethrough: "Зачёркнутый",
      uploading: "Загружаем…",
    },
    hideEmptyFields: "Скрыть пустые поля",
    hideFieldForType: "Не показывать для этого типа",
    insertBlock: "Вставить блок",
    moreActions: "Ещё действия",
    moreFields: { few: "Ещё {count} поля", many: "Ещё {count} полей", one: "Ещё {count} поле", other: "Ещё {count} поля" },
    saveFailed: "Не сохранилось",
    saved: "Всё сохранено",
    saving: "Сохраняем…",
    title: "Название",
    type: "Тип задачи",
    untitled: "Без названия",
  },
  itemArchive: {
    archiving: "Убираем…",
    description: "Задача уедет в корзину: оттуда её можно вернуть, а через отведённый срок она удалится насовсем.",
  },
  itemComments: {
    deleting: "Удаляем…",
  },
  itemTypes: {
    archiveCategory: "Убрать категорию",
    categoryName: "Название категории",
    configure: "Настроить",
    createFirst: "Заведите типы, с которыми работает команда, — потом добавляйте задачи.",
    newCategory: "Новая категория",
    newCategoryName: "Название новой категории",
    newType: "Новый тип",
    noActiveTypes: "Активных типов нет",
    noCategory: "Без категории",
    title: "Типы задач",
    typeCategory: "Категория типа",
    whatItIsPanel: "Типы общие для всей рабочей области — один и тот же тип работает на любой доске.",
    archive: "Убрать…",
    archiveConfirm: "Убрать этот тип?",
    archiveHint: "У задач, которые уже его используют, он останется, но новым задачам он больше не предложится.",
    category: "Категория",
    key: "Ключ",
    untitled: "Тип без названия",
    whatItIs: "Тип говорит, что это за работа: баг, история, гипотеза. Типы общие для всех пространств и досок.",
  },
  items: {
    title: "Задачи",
    whatItIs: "Задача существует один раз, а доски решают, где и как её показать.",
  },
  login: {
    continue: "Продолжить",
    email: "Электронная почта",
    emailCode: "Код из письма",
    emailPlaceholder: "vasha.pochta@company.com",
    error: {
      cancelled: "Вход отменён.",
      expired: "Ссылка для входа устарела. Попробуйте ещё раз.",
      invitation_invalid: "Приглашение больше не действует. Попросите новое.",
      invitation_mismatch: "Приглашение отправлено на другой адрес. Войдите с ним или попросите новое приглашение.",
      link_conflict: "Этот аккаунт уже связан с другой учёткой {provider}. Обратитесь к администратору.",
      no_email: "{provider} не передал адрес почты.",
      provider: "Не получилось войти через {provider}. Попробуйте через минуту.",
      service_account: "Этот адрес принадлежит служебной учётке, войти под ним нельзя.",
      unverified_email: "{provider} не подтвердил адрес почты.",
    },
    or: "или",
    providerHint: "Войдите рабочей учёткой. Доступ есть у сотрудников с ролью в админке.",
    providerOnly: "Вход",
    redeeming: "Входим…",
    withProvider: "Войти через {provider}",
    joinHint: "Введите адрес, на который пришло приглашение. После подтверждения аккаунт войдёт в рабочую область.",
    joinWorkspace: "Присоединиться к рабочей области",
    signIn: "Войти или создать аккаунт",
    signInHint: "Введите почту, чтобы продолжить. Новая рабочая область создаётся только для тех, кто заходит впервые и не по приглашению.",
    verifyCode: "Подтвердить код",
  },
  mcpClients: {
    "claude-code": {
      description: "Добавляет сервер в пользовательский конфиг — он будет доступен во всех проектах.",
      step1: "Выполните это в терминале.",
      step2: "Запустите новую сессию Claude Code.",
      step3: "Наберите /mcp и убедитесь, что impacto подключён.",
    },
    "claude-desktop": {
      description: "Claude Desktop подключается к локальным серверам, поэтому до этого он идёт через мост mcp-remote. Ставить ничего не нужно — npx скачает сам.",
      step1: "Настройки → Developer → Edit Config.",
      step2: "Добавьте это в файл, не удаляя уже прописанные серверы.",
      step3: "Полностью закройте Claude Desktop и откройте заново.",
    },
    "codex": {
      description: "Codex ходит по HTTP напрямую и умеет входить сам — если токена нет, настраивать больше нечего.",
      step1: "Выполните это в терминале.",
      step2: "Он предложит войти — подтвердите в открывшемся браузере.",
      step3: "Наберите `codex mcp list` и убедитесь, что impacto на месте.",
    },
    "cursor": {
      description: "Cursor умеет HTTP напрямую. Файл в проекте — для одного репозитория, в домашней папке — для всех.",
      step1: "Откройте ~/.cursor/mcp.json или .cursor/mcp.json в проекте.",
      step2: "Добавьте это в mcpServers.",
      step3: "Если сервер не появился, переоткройте настройки MCP.",
    },
    "vscode": {
      description: "VS Code требует явно указать транспорт.",
      step1: "Создайте .vscode/mcp.json в рабочей папке.",
      step2: "Вставьте это.",
      step3: "Запустите сервер из панели MCP.",
    },
    "windsurf": {
      description: "Windsurf называет адрес serverUrl, а не url.",
      step1: "Откройте настройки MCP или файл mcp_config.json.",
      step2: "Добавьте этот сервер.",
      step3: "Если сервер не появился, перезапустите панель MCP.",
    },
    "other": {
      description: "Любому клиенту с MCP поверх HTTP нужны только эти две строки. Если ваш клиент не умеет слать заголовок — возьмите вариант Claude Desktop выше: он превращает это в локальный сервер.",
      step1: "Добавьте MCP-сервер по HTTP (streamable).",
      step2: "Укажите этот адрес.",
      step3: "Клиент предложит войти. Если он так не умеет — передайте служебный токен в заголовке Authorization.",
    },
  },
  mcpPrompts: {
    describe: "Опиши мою рабочую область в Impacto: доски, статусы и что означают поля связей.",
    blocking: "Что сейчас блокирует работу в процессе? Пройди по связям.",
    changed: "Кратко: что изменилось в Impacto со вчерашнего дня.",
  },
  myWork: {
    open: "Открыть",
    title: "Мои задачи",
    whatItIs: "Сюда попадают задачи, где вы указаны в поле с человеком.",
  },
  activityFeed: {
    allSources: "Любой источник",
    connection: "Приложение",
    machinesOnly: "Всё, что не руками",
    source: "Через что",
    viaApp: "в интерфейсе",
    viaConnection: "через {name}",
    viaToken: "через {name}",
    actor: "Кто",
    allActors: "Все",
    allEvents: "Всё",
    correlationHint: "Всё, что вызвал один запрос, помечено одним и тем же id.",
    empty: "Под эти фильтры ничего не попало.",
    eventType: "Что произошло",
    filtered: "{count} из последних {limit} событий",
    period: "Когда",
    period7: "За 7 дней",
    period30: "За 30 дней",
    period24h: "За сутки",
    periodAll: "За всё время",
    serviceAccounts: "Служебные аккаунты",
    subtitle: "Кто и что менял в рабочей области — включая токены и служебные аккаунты.",
    title: "Активность",
    viaService: "служебный аккаунт",
  },
  table: {
    boardsCount: {
      one: "{count} доска",
      few: "{count} доски",
      many: "{count} досок",
      other: "{count} доски",
    },
    spacesCount: {
      one: "{count} пространство",
      few: "{count} пространства",
      many: "{count} пространств",
      other: "{count} пространства",
    },
    category: "Категория",
    created: "Создано",
    item: "Задача",
    key: "Ключ",
    name: "Название",
    type: "Тип",
  },
  dataTable: {
    collapseGroup: "Свернуть группу",
    columns: "Колонки",
    dragRow: "Перетащить строку",
    dropHere: "Перетащите задачу сюда",
    emptyGroup: "Пустая группа",
    selectAllRows: "Выбрать все строки",
    selectRow: "Выбрать строку",
    tableColumns: "Колонки таблицы",
    visibleColumns: "Показывать колонки",
  },
  datePicker: {
    clear: "Очистить",
    done: "Готово",
    end: "Конец",
    start: "Начало",
    time: "Время",
  },
  docTable: {
    addRemoveColumns: "Добавить или убрать колонки",
    addRemoveRows: "Добавить или убрать строки",
    columnActions: "Действия с колонкой",
    headerColumn: "Колонка-заголовок",
    headerRow: "Строка-заголовок",
    resizeLeft: "Изменить размер (левый край)",
    resizeRight: "Изменить размер (правый край)",
    rowActions: "Действия со строкой",
  },
  replay: {
    loading: "Загружаем историю спринта…",
    none: "Истории спринта пока нет",
    pause: "Пауза",
    play: "Воспроизвести",
  },
  typeDialog: {
    archiving: "Архивируем…",
    saveChanges: "Сохранить изменения",
    archiveType: "Архивировать тип",
    createType: "Создать тип",
    newType: "Новый тип",
    settings: "Настройки типа",
    typeName: "Название типа",
  },
  dashboards: {
    widgetSummaryPlaceholder: "например, Недельная сводка",
    widgetTitlePlaceholder: "например, Задачи под риском",
  },
  timeline: {
    emptyDraggable: "В этом диапазоне ничего не запланировано. Протяните по пустому месту, чтобы создать задачу.",
    emptyReadOnly: "В этом диапазоне ничего не запланировано. Сдвиньте таймлайн или проставьте задачам даты.",
  },
  docMenu: {
    columnAddAfter: "Вставить справа",
    columnAddBefore: "Вставить слева",
    columnClear: "Очистить колонку",
    columnDelete: "Удалить колонку",
    columnDuplicate: "Дублировать колонку",
    columnMoveAfter: "Сдвинуть вправо",
    columnMoveBefore: "Сдвинуть влево",
    rowAddAfter: "Вставить снизу",
    rowAddBefore: "Вставить сверху",
    rowClear: "Очистить строку",
    rowDelete: "Удалить строку",
    rowDuplicate: "Дублировать строку",
    rowMoveAfter: "Сдвинуть вниз",
    rowMoveBefore: "Сдвинуть вверх",
  },
  forms: {
    submit: "Отправить",
    titlePlaceholder: "Что нужно сделать?",
    viewFieldsHint: "Форма показывает видимые поля этого вида. Их состав меняется в настройках вида — связи и вычисляемые поля остаются внутренними.",
  },
  document: {
    commandHint: "Введите «/», чтобы вызвать команды…",
    headingPlaceholder: "Заголовок {level}",
    saveFailed: "Не удалось сохранить",
    saved: "Сохранено",
    saving: "Сохраняем…",
    staleReload: "Документ изменил кто-то ещё. Перезагрузите, чтобы получить свежую версию — несохранённые правки при этом пропадут.",
    startWriting: "Начните писать…",
    uploading: "Загружаем…",
  },
  nav: {
    activity: "Активность",
    boardNotFound: "Доска не нашлась в дереве",
    couldNotLoad: "Не удалось загрузить Impacto",
    folderNotFound: "Папка не нашлась в дереве",
    skipToContent: "Перейти к содержимому",
    createSpace: "Создать пространство",
    createWorkspace: "Создать рабочую область",
    fields: "Поля",
    inbox: "Входящие",
    integrations: "Интеграции",
    language: "Язык",
    myWork: "Мои задачи",
    search: "Поиск",
    signOut: "Выйти",
    spaces: "Пространства",
    team: "Команда",
    trash: "Корзина",
    types: "Типы",
    workspace: "Рабочая область",
    workspaceNavigation: "Навигация по рабочей области",
    workspaces: "Рабочие области",
  },
  publicPage: {
    formUnavailable: "Форма недоступна",
    linkDisabled: "Возможно, ссылку отключили. Попросите новую у того, кто ей поделился.",
    noItems: "Задач нет",
    submitAnother: "Отправить ещё",
    thanks: "Спасибо — отправлено!",
    viewUnavailable: "Этот вид недоступен",
  },
  search: {
    noMatches: "По запросу «{query}» ничего не нашлось.",
    placeholder: "Найти задачу…",
    title: "Поиск",
    typeTwo: "Введите хотя бы два символа — поиск идёт по всем доскам.",
  },
  shareLink: {
    anyoneCanSubmit: "Любой, у кого есть ссылка, сможет отправить заявку на эту доску без входа.",
    anyoneCanView: "Любой, у кого есть ссылка, увидит этот вид без входа.",
    disable: "Выключить",
    enable: "Включить",
    copy: "Скопировать ссылку",
    publicLink: "Публичная ссылка",
    publicReadOnly: "Публичная ссылка только для чтения",
  },
  space: {
    board: "Доска",
    description: "Описание",
    descriptionPlaceholder: "Доски команды и всё, чем она занимается",
    emptyTree: "Папок и досок пока нет.",
    folder: "Папка",
    name: "Название",
    namePlaceholder: "Разработка",
    private: "Личное",
    settings: "Настройки",
    shared: "Общее",
    sprintBoard: "Доска спринтов",
    sprintBoardExists: "В этом пространстве уже есть доска спринтов",
    whatItIsShort: "Пространства группируют доски по командам, отделам или направлениям работы.",
  },
  sprint: {
    statusProgressHint: "{name}: {done} из {total} готово",
    estimateSourcesHint: "Поля, которыми меряется объём спринта: числовые считаются очками, длительности — часами, варианты списка сопоставляются числам. Первый источник — основная ось, по ней считается velocity.",
    addItemsHint: "Добавьте задачи с досок этого пространства или вытащите их из спринта.",
    allAlreadyOnBoard: "Все задачи этого пространства уже на доске.",
    createSprint: "Создать спринт",
    hideMapping: "Скрыть сопоставление",
    mapping: "Сопоставление",
    newItemsLandHere: "Новые задачи этой доски попадают сюда.",
    noItemsMatch: "Под поиск ничего не подходит.",
    widgetQueuePlaceholder: "например, Горячая очередь",
    widgetTitlePlaceholder: "например, Фокус на сегодня",
    burndownAxisHint: "Остаток по «{label}»{unit} по дням против идеального темпа.",
    burndownItemsHint: "Остаток задач по дням против идеального темпа.",
    cycleTimeSummary: {
      one: "От старта до финиша по {count} завершённой задаче из закрытых спринтов.",
      few: "От старта до финиша по {count} завершённым задачам из закрытых спринтов.",
      many: "От старта до финиша по {count} завершённым задачам из закрытых спринтов.",
      other: "От старта до финиша по {count} завершённым задачам из закрытых спринтов.",
    },
    forecastFits: "задач в работе успевают к концу спринта",
    forecastRisk: {
      one: "При медианном темпе {count} задача не успевает к концу спринта.",
      few: "При медианном темпе {count} задачи не успевают к концу спринта.",
      many: "При медианном темпе {count} задач не успевают к концу спринта.",
      other: "При медианном темпе {count} задачи не успевают к концу спринта.",
    },
    hintAdded: "добавлено после старта",
    hintCarried: "перенесено при закрытии",
    hintOpen: "ещё в работе",
    hintPlanned: "запланировано до старта",
    hintRemaining: "осталось по этой оси",
    hintRemoved: "снято после старта",
    median: "медиана",
    percentile85: "85-й перцентиль",
    removedAt: "снято {date}",
    removedFromSprint: "Снято со спринта",
    removedHint: "Это было в обязательствах на момент Start и ушло из спринта позже.",
    scopeRemoved: "Объём снят",
    stayTooltip: {
      one: "{count} задача · {total} всего",
      few: "{count} задачи · {total} всего",
      many: "{count} задач · {total} всего",
      other: "{count} задачи · {total} всего",
    },
    velocityAxisHint: "Завершённые «{label}» за каждый закрытый спринт (снимок на момент закрытия).",
    velocityItemsHint: "Завершённые задачи за каждый закрытый спринт.",
    closedMoment: "Спринт закрыт",
    all: "Всё",
    active: "Идёт",
    addItems: "Добавить задачи",
    addSource: "Добавить источник",
    added: "Добавлено",
    allItems: "Все задачи",
    autoPlan: "Собрать следующий спринт автоматически",
    average: "Сколько задача в среднем стоит в статусе — куда на самом деле уходит время спринта.",
    backlog: "Бэклог",
    beginToday: "Начать сегодня",
    beginTodayHint: "Сдвигает согласованные даты так, чтобы спринт начался сегодня, длина сохраняется. Выключено — даты остаются как запланировали.",
    burndown: "Сгорание",
    byStatus: "По статусам",
    carried: "Перенесено",
    carriedOver: "Перенесено",
    closeFirst: "Закройте первый спринт — с него начнётся история скорости.",
    closed: "Закрыт",
    committed: "Взяли в спринт",
    complete: "Завершить",
    completeSprint: "Завершить спринт",
    createFirst: "Создайте первый спринт — с него начнётся планирование.",
    createInBacklog: "Создайте спринт во вкладке «Бэклог» — тогда здесь появятся сгорание и скорость.",
    cumulativeFlow: "Накопительный поток",
    cycleTime: "Время цикла",
    delete: "Удалить спринт",
    deleteHint: "Задачи вернутся в бэклог. Отменить это нельзя.",
    dismiss: "Скрыть",
    done: "Сделано",
    edit: "Изменить спринт",
    estimateSources: "Источники оценки",
    estimates: "Оценки",
    every2: "Раз в 2 недели",
    every3: "Раз в 3 недели",
    every4: "Раз в 4 недели",
    everyWeek: "Каждую неделю",
    exitReplay: "Выйти из перемотки",
    goal: "Цель (по желанию)",
    inProgress: "В работе",
    items: "Задачи",
    longestStays: "Дольше всего стоят",
    newSprint: "Новый спринт",
    noData: "Данных пока нет.",
    noSourcesYet: "Источников нет — отчёт считает задачи штуками. Добавьте поле, чтобы считать в очках или часах.",
    noSprints: "Спринтов пока нет",
    noSprintsToReport: "Не по чему строить отчёт",
    nothingCompleted: "В этом спринте ничего не завершили.",
    nothingHere: "По этому фильтру ничего нет.",
    nothingPlanned: "В спринт ничего не запланировали.",
    off: "Выключено",
    open: "Открытые",
    options: "Что сделать со спринтом",
    overdue: "Просрочен",
    planItLater: "Запланировать позже",
    planned: "Запланирован",
    releaseCutMoved: "Дата релиза сдвинулась вперёд",
    remaining: "Осталось",
    removeFromBoard: "Убрать с доски",
    replay: "Перемотка",
    replayCopyLink: "Ссылка на этот момент",
    replayHistory: "Что происходило в спринте",
    replayNext: "Следующий важный момент",
    replayPrevious: "Предыдущий важный момент",
    returnToPlanned: "Вернуть в запланированные",
    risks: "Риски",
    saveMapping: "Сохранить соответствие",
    scope: "Состав",
    scopeAdded: "Добавили в состав",
    searchItems: "Найти задачу…",
    sprint: "Спринт",
    sprintEmpty: "В спринте пусто.",
    sprintsBackToBack: "Спринты идут встык — даты не могут пересекаться с другими спринтами.",
    start: "Запустить спринт",
    theseMoveToo: "Эти спринты тоже сдвинутся:",
    timeInStatus: "Время в статусе",
    toDo: "Предстоит",
    velocity: "Скорость",
    whyClosingOpen: "Почему закрываем с незавершённой работой?",
    window: "Даты",
  },
  statuses: {
    closedIsSystem: "«Закрыто» — системный статус, его нельзя архивировать.",
    savingChanges: "Изменения сохраняются.",
    activeKeepsOne: "В «В работе» должен остаться хотя бы один статус.",
    addRules: "Добавить условия",
    clearRequirements: "Снять условия",
    notStartedKeepsOne: "В «Не начато» должен остаться хотя бы один статус.",
    rules: "Условия",
    saveRequirements: "Сохранить условия",
    active: "В работе",
    activeHint: "Работа, которая идёт прямо сейчас.",
    archive: "Убрать",
    changeColor: "Сменить цвет статуса",
    closed: "Закрыто",
    closedHint: "Работа окончательно закрыта.",
    custom: "Свой",
    customColor: "Свой цвет статуса",
    default: "По умолчанию",
    done: "Готово",
    doneHint: "Работа завершена, но ещё видна на доске.",
    makeDefault: "Сделать по умолчанию",
    moveItemsTo: "Перенести задачи в",
    newStatus: "Новый статус",
    notStarted: "Не начато",
    notStartedHint: "Работа, к которой ещё не приступали.",
    selectStatus: "Выберите статус",
    status: "Статус",
    statusColor: "Цвет статуса",
    title: "Статусы доски",
  },
  team: {
    admin: "Администратор",
    adminHint: "Управляет людьми, командами и всеми пространствами.",
    copyInvite: "Скопировать ссылку-приглашение",
    createFirstTeam: "Создайте первую команду",
    createTeam: "Создать команду",
    createTeamHint: "Назовите команду и добавьте участников, чтобы делиться с ними пространствами.",
    editTeamHint: "Переименуйте команду и выберите, кто в неё входит.",
    email: "Электронная почта",
    emailPlaceholder: "sotrudnik@company.com",
    guest: "Гость",
    guestHint: "Видит только то, чем с ним поделились.",
    invitationCreated: "Приглашение готово — отправьте эту ссылку:",
    awaitingSignIn: "Ждёт первого входа",
    invitationEmailFailed: "Приглашение сохранено, но письмо не ушло ({error}). Передайте ссылку сами.",
    invitations: "Приглашения",
    invite: "Пригласить людей",
    inviteDescription: "Отправьте ссылку-приглашение и выберите, что человеку можно делать.",
    inviteEmpty: "Пригласите кого-нибудь кнопкой выше — приглашение будет висеть здесь, пока его не примут.",
    inviteName: "Имя",
    inviteNameHint: "Как человек будет подписан на досках, пока не зашёл впервые. Можно не заполнять.",
    inviteNamePlaceholder: "Иван Петров",
    invitedOn: "Приглашён {date}",
    joined: "С {date}",
    manage: "Настроить",
    member: "Участник",
    memberHint: "Создаёт и меняет работу в общих пространствах.",
    members: "Участники",
    newTeam: "Новая команда",
    noMembers: "Пока никого не добавили.",
    noMembersMatch: "По запросу «{query}» никого нет.",
    noPending: "Непринятых приглашений нет",
    noTeams: "Команд пока нет",
    notSignedIn: "ещё не заходил",
    outsideRole: "вне этой роли",
    owner: "Владелец",
    ownerHint: "Может всё, включая оплату и передачу владения.",
    pending: "Ждут ответа",
    role: "Что можно делать",
    saveTeam: "Сохранить команду",
    searchMembers: "Найти участника",
    searchMembersPlaceholder: "Найти участника…",
    searchToAdd: "Найдите, кого добавить",
    sendInvite: "Отправить приглашение",
    serviceAccountCannotSignIn: "Войти под этим аккаунтом нельзя — он доступен только по токену.",
    serviceAccountCreate: "Добавить аккаунт",
    serviceAccountFailed: "Не удалось изменить сервисный аккаунт.",
    serviceAccountPlaceholder: "Деплой-бот, Ночная синхронизация…",
    serviceAccountRemove: "Удалить {name}",
    serviceAccounts: "Сервисные аккаунты",
    serviceAccountsWhat: "Аккаунт для интеграции, а не для человека.",
    serviceAccountsWhy: "Дайте ему доступ ровно к тем пространствам, которые нужны, и выпустите на него токен. Интеграция продолжит работать, когда люди меняют роли или уходят, а всё сделанное будет подписано его именем.",
    teamDelete: "Доступы, выданные команде, пропадут. То, что есть у людей лично, останется.",
    teamName: "Название команды",
    teamNamePlaceholder: "например: Продукт, Дизайн, Руководители",
    teams: "Команды",
    teamsWhat: "Давайте доступ группе, а не каждому по отдельности.",
    teamsWhatLong: "Соберите людей в команду — и делитесь пространствами, папками и досками сразу со всеми.",
    title: "Люди в рабочей области",
    whatItIs: "Приглашайте людей, выбирайте, что им можно, и объединяйте в команды — чтобы выдавать доступ один раз, а не каждому отдельно.",
    you: "Вы",
  },
  time: {
    daysAgo: {
      few: "{count} дня назад",
      many: "{count} дней назад",
      one: "{count} день назад",
      other: "{count} дня назад",
    },
    hoursAgo: {
      few: "{count} часа назад",
      many: "{count} часов назад",
      one: "{count} час назад",
      other: "{count} часа назад",
    },
    justNow: "только что",
    minutesAgo: {
      few: "{count} минуты назад",
      many: "{count} минут назад",
      one: "{count} минуту назад",
      other: "{count} минуты назад",
    },
  },
  trash: {
    purge: "Удалить навсегда",
    purgeItem: "Задача, её значения полей, комментарии и описание пропадут окончательно. Отменить это нельзя.",
    purgeConfirm: "Удалить «{title}» навсегда?",
    purgeBoard: "Доска, её виды, статусы и история спринтов исчезнут навсегда. Задачи, которые есть и на других досках, останутся.",
    empty: "Корзина пуста.",
    descriptionWithRetention: "Удалённое стирается навсегда через {days} дн. Доски остаются, пока вы не удалите их сами.",
    description: "Удалённое лежит здесь, пока вы не восстановите или не сотрёте это окончательно.",
    boards: "Доски",
    restore: "Восстановить",
    title: "Корзина",
    workItems: "Задачи",
  },
  webhooks: {
    active: "Отправляет",
    add: "Новый адрес",
    allEvents: "Всё",
    archive: "Убрать",
    archiveConfirm: "Убрать этот адрес?",
    archiveHint: "Туда больше ничего не уйдёт. Уже отправленное останется в истории.",
    created: "«{name}» готов. Скопируйте секрет подписи сейчас — он показывается только здесь.",
    delivered: "Доставлено",
    deliveries: "Последние отправки",
    deliveriesEmpty: "Пока ничего не отправляли.",
    empty: "Адресов пока нет.",
    event: {
      field_definition_archived: "Поле убрали",
      field_definition_configured: "Изменили настройки поля",
      field_definition_created: "Создали поле",
      field_definition_renamed: "Переименовали поле",
      field_value_changed: "Изменилось значение поля",
      invitation_accepted: "Приглашение приняли",
      invitation_created: "Кого-то пригласили",
      invitation_revoked: "Приглашение отозвали",
      member_removed: "Человека убрали",
      member_role_updated: "Изменили, что человеку можно",
      scope_access_updated: "Изменили доступ",
      sprint_closed: "Спринт закрыли",
      sprint_scope_added: "Задачу добавили в спринт",
      sprint_scope_removed: "Задачу убрали из спринта",
      sprint_start_reverted: "Старт спринта откатили",
      sprint_started: "Спринт запустили",
      status_changed: "Сменился статус",
      team_archived: "Команду убрали",
      team_created: "Создали команду",
      team_updated: "Изменили команду",
      work_object_archived: "Задачу убрали в корзину",
      work_object_created: "Создали задачу",
      work_object_restored: "Задачу вернули",
    },
    eventsAccess: "Люди и доступ",
    eventsFields: "Поля",
    eventsSprints: "Спринты",
    eventsWork: "Задачи",
    failed: "Не дошло",
    groupChoose: "О чём сообщать",
    groupChooseHint: "Если ничего не отметить — будет приходить всё.",
    name: "Название",
    namePlaceholder: "Бот деплоя",
    paused: "На паузе",
    pausedHint: "Адрес на паузе сохраняет настройки, но ничего не получает.",
    pending: "В очереди",
    replay: "Отправить ещё раз",
    save: "Сохранить",
    secret: "Секрет подписи",
    secretHint: "К каждому запросу прикладывается заголовок `X-Impacto-Signature` — HMAC-SHA256 от времени и тела, посчитанный этим секретом. Проверяйте его у себя, чтобы сообщение нельзя было подделать.",
    title: "Отправлять события наружу",
    url: "Адрес",
    urlPlaceholder: "https://example.com/impacto-hook",
    whatItIs: "Impacto может отправить POST в другую систему в момент, когда здесь что-то происходит: боту в чат, в сборку, в ваш сервис. При сбое повторяет и показывает каждую попытку.",
  },
  widgets: {
    add: "Добавить виджет",
    amberWhen: "Жёлтый, когда…",
    ascending: "По возрастанию",
    atLeast: "Не меньше",
    atMost: "Не больше",
    average: "Среднее по полю",
    board: "Доска",
    boardOrder: "Порядок доски",
    broken: "Виджет сломан",
    brokenEdit: "Виджет сломан — поправьте его.",
    brokenFilter: "Фильтр сломан",
    brokenFilterEdit: "Фильтр сломан — поправьте виджет.",
    condition: "Условие",
    count: "Количество задач",
    descending: "По убыванию",
    direction: "Порядок",
    eachWidget: "Каждый виджет смотрит на одну доску, при желании — через фильтр.",
    empty: "Дашборд пустой — добавьте показатели, списки задач и заметки по любой доске.",
    evaluatedAgainst: "Считается по спринту, выбранному в отчёте.",
    field: "Поле",
    filter: "Фильтр",
    greaterThan: "Больше",
    highlight: "Подсветка",
    itemList: "Список задач",
    lessThan: "Меньше",
    metric: "Показатель",
    never: "Никогда",
    note: "Заметка",
    nothingInSprint: "В этом спринте ничего не подходит.",
    nothingMatches: "Ничего не подходит.",
    options: "Что сделать с виджетом",
    pickField: "Выберите поле",
    redWhen: "Красный, когда…",
    showTop: "Показать первые",
    sortBy: "Сортировать по",
    sum: "Сумма по полю",
    text: "Текст",
    title: "Заголовок",
    value: "Значение",
    whatMatters: "Что важно…",
    whatMattersToday: "Что важно сегодня…",
  },
};
